import React, { useState, useEffect } from 'react';
import { 
  Folder, FolderPlus, MoreVertical, Trash2, Edit2, 
  FileText, Users, ChevronRight, X, Check
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FOLDER_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
  '#ec4899', '#06b6d4', '#84cc16'
];

const FoldersPage = () => {
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newFolder, setNewFolder] = useState({ name: '', color: '#3b82f6', description: '' });
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderItems, setFolderItems] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);

  useEffect(() => {
    if (user) fetchFolders();
  }, [user]);

  const fetchFolders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/folders?user_id=${user.id}`);
      const data = await res.json();
      setFolders(data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const createFolder = async () => {
    if (!newFolder.name.trim()) {
      toast.error('Nom du dossier requis');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/folders?user_id=${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newFolder)
      });
      if (res.ok) {
        toast.success('Dossier créé !');
        setShowCreate(false);
        setNewFolder({ name: '', color: '#3b82f6', description: '' });
        fetchFolders();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de création');
    }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Supprimer ce dossier ? Les éléments ne seront pas supprimés.')) return;
    try {
      const res = await fetch(`${API_URL}/api/folders/${folderId}?user_id=${user.id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Dossier supprimé');
        fetchFolders();
        if (selectedFolder?.id === folderId) {
          setSelectedFolder(null);
          setFolderItems(null);
        }
      }
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const openFolder = async (folder) => {
    setSelectedFolder(folder);
    try {
      const res = await fetch(`${API_URL}/api/folders/${folder.id}/items?user_id=${user.id}`);
      const data = await res.json();
      setFolderItems(data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Dossiers</h1>
          <p className="text-slate-500">Organisez vos factures et clients</p>
        </div>
        <Button 
          onClick={() => setShowCreate(true)}
          className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
        >
          <FolderPlus className="h-4 w-4 mr-2" />
          Nouveau dossier
        </Button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <Card className="border-blue-500/50 bg-blue-50/50 dark:bg-blue-900/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 dark:text-white">Nouveau dossier</h3>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Nom du dossier"
                value={newFolder.name}
                onChange={(e) => setNewFolder({ ...newFolder, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
              <div>
                <label className="text-sm text-slate-600 dark:text-slate-400 mb-2 block">Couleur</label>
                <div className="flex gap-2">
                  {FOLDER_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewFolder({ ...newFolder, color })}
                      className={`w-8 h-8 rounded-full transition-transform ${newFolder.color === color ? 'scale-125 ring-2 ring-offset-2 ring-slate-400' : ''}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <input
                type="text"
                placeholder="Description (optionnel)"
                value={newFolder.description}
                onChange={(e) => setNewFolder({ ...newFolder, description: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
                <Button onClick={createFolder}>
                  <Check className="h-4 w-4 mr-2" />
                  Créer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Folders Grid */}
      {folders.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Folder className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Aucun dossier</h3>
            <p className="text-slate-500 mb-4">Créez des dossiers pour organiser vos factures et clients</p>
            <Button onClick={() => setShowCreate(true)}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Créer mon premier dossier
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {folders.map((folder) => (
            <Card 
              key={folder.id} 
              className="group hover:shadow-lg transition-all cursor-pointer border-slate-200 dark:border-slate-700 hover:border-blue-500/50"
              onClick={() => openFolder(folder)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${folder.color}20` }}
                  >
                    <Folder className="h-6 w-6" style={{ color: folder.color }} />
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500 transition-opacity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1 truncate">{folder.name}</h3>
                {folder.description && (
                  <p className="text-sm text-slate-500 truncate mb-2">{folder.description}</p>
                )}
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    {folder.invoice_count || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {folder.client_count || 0}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Folder Detail Modal */}
      {selectedFolder && folderItems && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedFolder(null)}>
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${selectedFolder.color}20` }}
                >
                  <Folder className="h-5 w-5" style={{ color: selectedFolder.color }} />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900 dark:text-white">{selectedFolder.name}</h2>
                  <p className="text-sm text-slate-500">{folderItems.total_items} élément(s)</p>
                </div>
              </div>
              <button onClick={() => setSelectedFolder(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[60vh]">
              {folderItems.total_items === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Folder className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>Dossier vide</p>
                  <p className="text-sm">Ajoutez des factures ou clients depuis leurs pages respectives</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {folderItems.invoices.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Factures ({folderItems.invoices.length})
                      </h3>
                      <div className="space-y-2">
                        {folderItems.invoices.map((inv) => (
                          <div key={inv.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">{inv.invoice_number}</p>
                              <p className="text-sm text-slate-500">{inv.client_name}</p>
                            </div>
                            <Badge>{inv.total_ttc}€</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {folderItems.clients.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Clients ({folderItems.clients.length})
                      </h3>
                      <div className="space-y-2">
                        {folderItems.clients.map((cli) => (
                          <div key={cli.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">{cli.name}</p>
                              <p className="text-sm text-slate-500">{cli.email}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FoldersPage;
